import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Package, AlertTriangle, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';

const Landing = () => {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState('mexico');

  const countries = [
    { id: 'mexico', name: 'México', flag: '🇲🇽', code: 'MX' },
    { id: 'colombia', name: 'Colombia', flag: '🇨🇴', code: 'CO' },
    { id: 'argentina', name: 'Argentina', flag: '🇦🇷', code: 'AR' },
    { id: 'chile', name: 'Chile', flag: '🇨🇱', code: 'CL' },
    { id: 'peru', name: 'Perú', flag: '🇵🇪', code: 'PE' },
    { id: 'brasil', name: 'Brasil', flag: '🇧🇷', code: 'BR' }
  ];

  const features = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Rentabilidad Real',
      description: 'Calcula tu utilidad neta considerando comisiones, envíos, COGS e impuestos automáticamente.',
      bgColor: 'bg-blue-500/10',
      iconColor: 'text-blue-400'
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: 'Control de Inventario',
      description: 'Diferencia entre FULL y bodega propia. Alertas cuando el stock alcance para menos de 7 días.',
      bgColor: 'bg-blue-500/10',
      iconColor: 'text-blue-400'
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: 'Alertas de Costos',
      description: 'Detecta automáticamente cuando los costos de envío superan el 20% del precio del producto.',
      bgColor: 'bg-red-500/10',
      iconColor: 'text-red-400'
    }
  ];

  const handleConnect = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white">
      {/* Header */}
      <header className="border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">MeLi Pro Analytics</h1>
          <Button 
            variant="outline" 
            className="border-gray-700 hover:bg-gray-800 hover:border-gray-600 transition-all duration-300"
            onClick={() => navigate('/login')}
          >
            Iniciar Sesión
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <div className="inline-block mb-6">
            <span className="px-4 py-2 bg-indigo-500/20 border border-indigo-500/30 rounded-full text-sm text-indigo-300">
              Control Total de Rentabilidad
            </span>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Maximiza tus{' '}
            <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-500 text-transparent bg-clip-text">
              Ganancias
            </span>
            {' '}en<br />Mercado Libre
          </h2>
          
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-12">
            Calcula tu utilidad real, controla costos de envío, gestiona inventario y<br />
            toma decisiones basadas en datos precisos.
          </p>

          {/* Country Selector Card */}
          <div className="max-w-xl mx-auto bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8 shadow-2xl">
            <h3 className="text-2xl font-semibold mb-6">Comienza Ahora</h3>
            
            <p className="text-gray-400 mb-4 text-left">Selecciona tu país:</p>
            
            <div className="grid grid-cols-2 gap-3 mb-6">
              {countries.map((country) => (
                <button
                  key={country.id}
                  onClick={() => setSelectedCountry(country.id)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all duration-300 ${
                    selectedCountry === country.id
                      ? 'bg-indigo-600 border-indigo-500 shadow-lg shadow-indigo-500/20'
                      : 'bg-gray-800/50 border-gray-700 hover:border-gray-600 hover:bg-gray-800'
                  }`}
                >
                  <span className="text-2xl">{country.flag}</span>
                  <span className="font-medium">{country.name}</span>
                </button>
              ))}
            </div>

            <Button 
              onClick={handleConnect}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold py-6 rounded-lg shadow-lg shadow-indigo-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/40 hover:scale-[1.02]"
            >
              Conectar con Mercado Libre
            </Button>

            <p className="text-sm text-gray-500 mt-4">
              Seguro y encriptado. Nunca almacenamos tu contraseña.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6 hover:border-gray-600/50 transition-all duration-300 hover:shadow-xl group"
            >
              <div className={`inline-flex p-3 rounded-lg ${feature.bgColor} ${feature.iconColor} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
              <p className="text-gray-400 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Integration Card */}
          <div
            className="relative bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6 overflow-hidden hover:border-gray-600/50 transition-all duration-300 hover:shadow-xl group"
          >
            <div className="absolute inset-0 opacity-20">
              <img 
                src="https://images.unsplash.com/photo-1553413077-190dd305871c?w=800&auto=format&fit=crop&q=60" 
                alt="Warehouse"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10">
              <div className="inline-flex p-3 rounded-lg bg-gray-800/80 backdrop-blur-sm mb-4">
                <Package className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Integración Directa</h3>
              <p className="text-gray-300 leading-relaxed">
                Conecta tu cuenta de Mercado Libre y obtén datos en tiempo real de tus ventas, envíos y productos.
              </p>
            </div>
          </div>

          {/* Multi-Country Card */}
          <div
            className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl p-6 hover:shadow-xl hover:shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] group"
          >
            <div className="inline-flex p-3 rounded-lg bg-white/20 backdrop-blur-sm mb-4 group-hover:scale-110 transition-transform duration-300">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-3">Multi-País</h3>
            <p className="text-white/90 leading-relaxed">
              Soporte para toda Latinoamérica con conversión automática de moneda.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            © 2024 MeLi Pro Analytics. Datos seguros y encriptados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;